import { resolvePackingStationLocationCode } from '@modules/Packaging/PackingStations/List/utils/resolvePackingStationLocationCode';
import { normalizePackingScanCode } from './packingAssignKey';
import { Order, PackagingCategory, PackagingDetail, PackingPayloadPackagingItem } from '../models';
import {
  computeOrderPackagingRequirements,
  packagingAssignKey,
  PackagingRequirement,
  sumAssignedForRequirement,
} from './packagingRequirements';

/** Ubicación del puesto con packaging embebido (GET /packing-stations/:id → locations[]) */
export interface PackingStationLocationEntry {
  id: number;
  packing_station_id: number;
  location_id?: number;
  code?: string;
  location?: { id: number; code: string };
  packaging?: {
    id: number;
    code?: string;
    name?: string;
    category?: PackagingCategory;
    product_id?: number | null;
    image_url?: string | null;
    /** ID del grupo de cajas al que pertenece este packaging (campo box_group_id de la API) */
    box_group_id?: number | null;
    box_group?: { id: number; name: string } | null;
  };
}

export const STATION_SCAN_CATEGORIES: PackagingCategory[] = ['customer_box', 'marketing_item'];

export const requirementUsesStationScan = (req: PackagingRequirement): boolean =>
  req.category != null && STATION_SCAN_CATEGORIES.includes(req.category);

const orderPackagingsHaveStationCategory = (order: Order): boolean =>
  order.order_packagings.some((op) => {
    const cat = op.packaging?.category as PackagingCategory | undefined;
    return cat != null && STATION_SCAN_CATEGORIES.includes(cat);
  });

export const orderRequiresPackingStation = (
  order: Order | null | undefined,
  packagings: PackagingDetail[],
): boolean => {
  if (!order) return false;
  if (computeOrderPackagingRequirements(order, packagings).some(requirementUsesStationScan)) return true;
  // Fallback: la categoría del packaging puede no haberse propagado al requirement (grupo mixto)
  return orderPackagingsHaveStationCategory(order);
};

export const ordersRequirePackingStation = (orders: Order[], packagings: PackagingDetail[]): boolean =>
  orders.some((order) => orderRequiresPackingStation(order, packagings));

export const orderHasPendingStationPackaging = (
  order: Order | null | undefined,
  packagings: PackagingDetail[],
  assignedPackagingQuantities: Record<string, number>,
): boolean => {
  if (!order) return false;

  const fromRequirements = computeOrderPackagingRequirements(order, packagings).some((req) => {
    if (!requirementUsesStationScan(req)) return false;
    return sumAssignedForRequirement(req, order.id, assignedPackagingQuantities) < req.qty;
  });
  if (fromRequirements) return true;

  // Fallback: comprobar directamente en order_packagings (categoría puede no propagarse al requirement)
  const pendingFromOrderPackagings = order.order_packagings.some((op) => {
    const cat = op.packaging?.category as PackagingCategory | undefined;
    if (cat && STATION_SCAN_CATEGORIES.includes(cat)) {
      if (op.packaging_id == null) return false;
      const assigned = assignedPackagingQuantities[packagingAssignKey(order.id, op.packaging_id)] ?? 0;
      return assigned < op.qty;
    }

    // Caja por grupo (packaging_id null): escaneo por ubicación del puesto
    if (op.box_group_id != null && op.packaging_id == null) {
      const groupReq = computeOrderPackagingRequirements(order, packagings).find(
        (req) => req.type === 'group' && req.key === `group:${op.box_group_id}`,
      );
      if (!groupReq) return false;
      return sumAssignedForRequirement(groupReq, order.id, assignedPackagingQuantities) < groupReq.qty;
    }

    return false;
  });
  if (pendingFromOrderPackagings) return true;

  return false;
};

export const getStationLocationCode = (entry: PackingStationLocationEntry): string =>
  resolvePackingStationLocationCode(entry);

export const findStationLocationByCode = (
  locations: PackingStationLocationEntry[],
  scannedCode: string,
): PackingStationLocationEntry | undefined => {
  const code = normalizePackingScanCode(scannedCode);
  if (!code) return undefined;
  return locations.find((entry) => normalizePackingScanCode(getStationLocationCode(entry)) === code);
};

export interface StationLocationScanMatch {
  requirement: PackagingRequirement;
  packagingIdToAssign: number;
  payloadItem: Omit<PackingPayloadPackagingItem, 'qty'>;
  displayOption: {
    packagingId: number;
    productId?: number;
    code?: string;
    name?: string;
    imageUrl?: string;
  };
}

/** Valida que la ubicación escaneada del puesto satisface un requerimiento pendiente customer_box / marketing_item. */
export const findRequirementForStationLocation = (
  stationLocation: PackingStationLocationEntry,
  order: Order,
  packagings: PackagingDetail[],
  assignedPackagingQuantities: Record<string, number>,
): StationLocationScanMatch | undefined => {
  const locationCode = getStationLocationCode(stationLocation);
  if (!locationCode) return undefined;

  const stationPkg = stationLocation.packaging;
  if (!stationPkg?.id) return undefined;

  const stationCategory = stationPkg.category;
  // Solo procesar categorías que usan escaneo por puesto
  if (!stationCategory || !STATION_SCAN_CATEGORIES.includes(stationCategory)) return undefined;

  const stationBoxGroupId = stationPkg.box_group_id ?? null;

  const buildDisplayOption = (): StationLocationScanMatch['displayOption'] => ({
    packagingId: stationPkg.id,
    productId: stationPkg.product_id ?? undefined,
    code: stationPkg.code,
    name: stationPkg.name,
    imageUrl: stationPkg.image_url ?? undefined,
  });

  const requirements = computeOrderPackagingRequirements(order, packagings);

  for (const req of requirements) {
    // Derivar el box_group_id del requirement (key = 'group:4')
    const reqBoxGroupId = req.type === 'group' && req.key.startsWith('group:') ? Number(req.key.slice(6)) : null;

    // Aceptar el requirement si:
    //  (a) está categorizado correctamente como station-scan, O
    //  (b) es un group requirement con el mismo box_group_id que el packaging del puesto
    const isStationReq = requirementUsesStationScan(req);
    const sharesBoxGroup = reqBoxGroupId != null && stationBoxGroupId != null && reqBoxGroupId === stationBoxGroupId;

    if (!isStationReq && !sharesBoxGroup) continue;

    const assigned = sumAssignedForRequirement(req, order.id, assignedPackagingQuantities);
    if (assigned >= req.qty) continue;

    // Comprobar si el packaging del puesto puede satisfacer este requerimiento:
    //  · por ID directo en members/options, O
    //  · por box_group_id coincidente (cubre casos donde el catálogo no incluye el packaging en el grupo)
    const inMembers = req.members.includes(stationPkg.id);
    const inOptions = (req.options || []).some(
      (opt) =>
        opt.packagingId === stationPkg.id || (stationPkg.product_id != null && opt.productId === stationPkg.product_id),
    );

    if (!inMembers && !inOptions && !sharesBoxGroup) continue;

    const matchedOption =
      (req.options || []).find(
        (opt) =>
          opt.packagingId === stationPkg.id ||
          (stationPkg.product_id != null && opt.productId === stationPkg.product_id),
      ) ?? buildDisplayOption();

    return {
      requirement: req,
      packagingIdToAssign: stationPkg.id,
      payloadItem: { location_code: locationCode },
      displayOption: matchedOption,
    };
  }

  // Fallback: si el cálculo de requirements no lo detectó (p.ej. category mal heredada del grupo),
  // comparar directamente contra order_packagings por packaging_id.
  for (const op of order.order_packagings) {
    if (op.packaging_id !== stationPkg.id) continue;

    const assigned = assignedPackagingQuantities[packagingAssignKey(order.id, stationPkg.id)] ?? 0;
    if (assigned >= op.qty) continue;

    return {
      requirement: {
        type: 'exact' as const,
        key: `exact:${stationPkg.id}`,
        qty: op.qty,
        members: [stationPkg.id],
        name: stationPkg.name ?? `Packaging ${stationPkg.id}`,
        category: stationCategory,
      },
      packagingIdToAssign: stationPkg.id,
      payloadItem: { location_code: locationCode },
      displayOption: buildDisplayOption(),
    };
  }

  return undefined;
};

export type AssignedPackagingScanDetail = {
  packagingId: number;
  productId?: number;
  code?: string;
  name?: string;
  imageUrl?: string;
};

export const packagingScanDetailKey = (orderId: number, packagingId: number) =>
  packagingAssignKey(orderId, packagingId);
